// SchematicLiftDiagram.tsx — Rigging-Calc-style SCHEMATIC lift diagram
// (Brian 2026-09-08 sprint, fact 272): FIXED-size tower art per structure type;
// actual heights/distances are carried by drafting dimension callouts
// (H_TB, H_L, D_H, D_L, D_T), never by drawing scale. Component art is big and
// recognizable: pulley blocks with sheaves, truck-mounted capstan winch, trolley
// riding the rope, load box. Attachment art (panel point / face basket /
// antenna mount / mast-pipe rooster head) and tag art (straight / self /
// static) compose independently. Black line-art; dash pattern + labels
// distinguish ropes (no color); force callouts are the only color (blue).
// Guyed-tower wires render VERY FAINT (Brian: never compete with working ropes).
import type { LiftDetail, LiftComputed } from '@/types/planData';

export interface SchematicLiftDiagramProps {
  lift: LiftDetail;
  computed: LiftComputed;
  width?: number;
  height?: number;
  className?: string;
  forceOverlay?: boolean;
}

const BLUE = '#1a237e';
const FAINT = '#e3e3e3';   // guy wires, bracing hints
const GRAY = '#9a9a9a';    // extension lines

// fixed art geometry (px) — the drawing never scales with real feet
const VW = 640;
const VH = 640;
// Crop the empty top/bottom bands so the schematic fills the box on phones
// (Brian 2026-09-09: 'huge amounts of space being taken up for no reason').
const CROP_TOP = 70;
const CROP_BOTTOM = 14;
const GROUND_Y = 548;
const TOWER_TOP_Y = 96;
const TOWER_X = 208;
const TOWER_BASE_HALF = 26;
const TOWER_TOP_HALF = 9;
const RIGHT_LIMIT = 596;

function fmt(n: number | null | undefined, d = 1): string {
  return n == null ? '—' : n.toFixed(d);
}

// --- small art helpers (local px coords) -------------------------------------
function SheaveBlock({ x, y, s = 1 }: { x: number; y: number; s?: number }) {
  // recognizable pulley block: shell + sheave circle + shackle bow above
  const w = 9 * s, h = 13 * s;
  return (
    <g stroke="black" fill="white" strokeWidth={1.4}>
      <path d={`M ${x - w / 2} ${y - h / 2} h ${w} v ${h} h ${-w} Z`} />
      <circle cx={x} cy={y} r={4.2 * s} fill="none" strokeWidth={1.1} />
      <circle cx={x} cy={y} r={1.1 * s} fill="black" stroke="none" />
      <path d={`M ${x} ${y - h / 2 - 5 * s} v ${5 * s}`} fill="none" />
      <path d={`M ${x - 3.4 * s} ${y - h / 2 - 5 * s} a 3.4 3.4 0 1 1 6.8 0`} fill="none" />
    </g>
  );
}

function TruckWinch({ x, y }: { x: number; y: number }) {
  // truck-mounted capstan winch, side view, drum at bed rear
  return (
    <g stroke="black" fill="white" strokeWidth={1.4}>
      <path d={`M ${x - 34} ${y} h 52 v -13 h -52 Z`} />            {/* bed */}
      <path d={`M ${x + 18} ${y} v -13 h 9 l 7 8 v 5`} fill="none" /> {/* cab */}
      <circle cx={x - 22} cy={y + 1} r={4.6} />                      {/* wheels */}
      <circle cx={x + 8} cy={y + 1} r={4.6} />
      <circle cx={x - 40} cy={y - 17} r={7.5} />                     {/* capstan drum */}
      <circle cx={x - 40} cy={y - 17} r={2.4} fill="black" stroke="none" />
      <path d={`M ${x - 40} ${y - 9.5} v 9.5`} fill="none" />        {/* drum stand */}
    </g>
  );
}

function TrolleyBlock({ x, y }: { x: number; y: number }) {
  return (
    <g stroke="black" fill="white" strokeWidth={1.3}>
      <path d={`M ${x - 7} ${y - 5} h 14 v 10 h -14 Z`} />
      <circle cx={x} cy={y} r={3.2} fill="none" strokeWidth={1} />
    </g>
  );
}

function LoadBox({ x, y }: { x: number; y: number }) {
  return (
    <g stroke="black" fill="white" strokeWidth={1.4}>
      <path d={`M ${x - 13} ${y} h 26 v 18 h -26 Z`} />
      <path d={`M ${x - 13} ${y} l 26 18 M ${x + 13} ${y} l -26 18`} stroke={FAINT} strokeWidth={1} />
      <path d={`M ${x} ${y - 7} v 7`} />
      <path d={`M ${x - 3.5} ${y - 7} a 3.5 3.5 0 1 1 7 0`} fill="none" />
    </g>
  );
}

function DimV({ x, y1, y2, label }: { x: number; y1: number; y2: number; label: string }) {
  // vertical dimension: extension ticks + arrows + rotated label
  const mid = (y1 + y2) / 2;
  return (
    <g stroke={GRAY} strokeWidth={0.9} fill="none">
      <line x1={x - 4} y1={y1} x2={x + 4} y2={y1} />
      <line x1={x - 4} y1={y2} x2={x + 4} y2={y2} />
      <line x1={x} y1={y1} x2={x} y2={y2} />
      <path d={`M ${x} ${y1} l -2.6 6 M ${x} ${y1} l 2.6 6`} stroke="black" strokeWidth={1} />
      <path d={`M ${x} ${y2} l -2.6 -6 M ${x} ${y2} l 2.6 -6`} stroke="black" strokeWidth={1} />
      <text
        x={x - 6} y={mid} fontSize={9} fontFamily="monospace" fill="black" stroke="white"
        strokeWidth={2.5} paintOrder="stroke" textAnchor="middle"
        transform={`rotate(-90 ${x - 6} ${mid})`}
      >
        {label}
      </text>
    </g>
  );
}

function DimH({ y, x1, x2, label }: { y: number; x1: number; x2: number; label: string }) {
  const mid = (x1 + x2) / 2;
  return (
    <g stroke={GRAY} strokeWidth={0.9} fill="none">
      <line x1={x1} y1={y - 4} x2={x1} y2={y + 4} />
      <line x1={x2} y1={y - 4} x2={x2} y2={y + 4} />
      <line x1={x1} y1={y} x2={x2} y2={y} />
      <path d={`M ${x1} ${y} l 6 -2.6 M ${x1} ${y} l 6 2.6`} stroke="black" strokeWidth={1} />
      <path d={`M ${x2} ${y} l -6 -2.6 M ${x2} ${y} l -6 2.6`} stroke="black" strokeWidth={1} />
      <text x={mid} y={y - 6} fontSize={9} fontFamily="monospace" fill="black" stroke="white" strokeWidth={2.5} paintOrder="stroke" textAnchor="middle">
        {label}
      </text>
    </g>
  );
}

function Label({ x, y, text, color = 'black', fs = 9.5, anchor = 'start' as const }: { x: number; y: number; text: string; color?: string; fs?: number; anchor?: 'start' | 'middle' | 'end' }) {
  return (
    <text x={x} y={y} fontSize={fs} fontFamily="monospace" fill={color} stroke="white" strokeWidth={2.6} paintOrder="stroke" textAnchor={anchor} style={{ letterSpacing: '0.4px' }}>
      {text}
    </text>
  );
}

// --- tower art per structure type (fixed schematic size) ----------------------
function TowerArt({ type, blockY }: { type: string; blockY: number }) {
  const H = GROUND_Y - TOWER_TOP_Y;
  const parts: React.ReactNode[] = [];
  const halfAt = (y: number) => {
    const f = (GROUND_Y - y) / H; // 0 at grade → 1 at top
    return TOWER_BASE_HALF + (TOWER_TOP_HALF - TOWER_BASE_HALF) * f;
  };
  if (type === 'monopole' || type === 'mast-pipe') {
    const bw = type === 'monopole' ? 12 : 7;
    const tw = type === 'monopole' ? 7 : 4.5;
    parts.push(
      <path key="pole" d={`M ${TOWER_X - bw} ${GROUND_Y} L ${TOWER_X - tw} ${TOWER_TOP_Y} L ${TOWER_X + tw} ${TOWER_TOP_Y} L ${TOWER_X + bw} ${GROUND_Y} Z`} fill="white" stroke="black" strokeWidth={1.5} />,
      <line key="flange" x1={TOWER_X - bw - 5} y1={GROUND_Y} x2={TOWER_X + bw + 5} y2={GROUND_Y} stroke="black" strokeWidth={2} />,
    );
    if (type === 'monopole') {
      // faint seam lines
      for (let i = 1; i < 5; i++) {
        const y = GROUND_Y - (H * i) / 5;
        parts.push(<line key={`s${i}`} x1={TOWER_X - halfAt(y) * 0.55} y1={y} x2={TOWER_X + halfAt(y) * 0.55} y2={y} stroke={FAINT} strokeWidth={1} />);
      }
    }
  } else {
    // lattice: self-support / guyed — tapered legs + faint X bracing + panel points
    const legs = `M ${TOWER_X - TOWER_BASE_HALF} ${GROUND_Y} L ${TOWER_X - TOWER_TOP_HALF} ${TOWER_TOP_Y} M ${TOWER_X + TOWER_BASE_HALF} ${GROUND_Y} L ${TOWER_X + TOWER_TOP_HALF} ${TOWER_TOP_Y}`;
    parts.push(<path key="legs" d={legs} fill="none" stroke="black" strokeWidth={1.7} />);
    const panels = 9;
    for (let i = 0; i < panels; i++) {
      const y0 = GROUND_Y - (H * i) / panels;
      const y1 = GROUND_Y - (H * (i + 1)) / panels;
      const x0L = TOWER_X - halfAt(y0), x0R = TOWER_X + halfAt(y0);
      const x1L = TOWER_X - halfAt(y1), x1R = TOWER_X + halfAt(y1);
      parts.push(
        <g key={`p${i}`} stroke={FAINT} strokeWidth={1} fill="none">
          <line x1={x0L} y1={y0} x2={x1R} y2={y1} />
          <line x1={x0R} y1={y0} x2={x1L} y2={y1} />
        </g>,
        <line key={`h${i}`} x1={x1L} y1={y1} x2={x1R} y2={y1} stroke="#c9c9c9" strokeWidth={1} />,
      );
    }
    // base plates
    parts.push(
      <g key="base" stroke="black" strokeWidth={2}>
        <line x1={TOWER_X - TOWER_BASE_HALF - 6} y1={GROUND_Y} x2={TOWER_X - TOWER_BASE_HALF + 6} y2={GROUND_Y} />
        <line x1={TOWER_X + TOWER_BASE_HALF - 6} y1={GROUND_Y} x2={TOWER_X + TOWER_BASE_HALF + 6} y2={GROUND_Y} />
      </g>,
    );
    // top platform + antenna mast
    const pw = TOWER_TOP_HALF * 2.4;
    parts.push(
      <g key="top" stroke="black" strokeWidth={1.4} fill="none">
        <line x1={TOWER_X - pw} y1={TOWER_TOP_Y} x2={TOWER_X + pw} y2={TOWER_TOP_Y} />
        <line x1={TOWER_X} y1={TOWER_TOP_Y} x2={TOWER_X} y2={TOWER_TOP_Y - 22} />
        <line x1={TOWER_X - 3} y1={TOWER_TOP_Y - 22} x2={TOWER_X + 3} y2={TOWER_TOP_Y - 22} />
      </g>,
    );
    if (type === 'guyed') {
      // VERY FAINT guy wires (Brian: must never compete with working ropes)
      for (const gy of [0.4, 0.7]) {
        const y = GROUND_Y - H * gy;
        const xAt = halfAt(y);
        parts.push(
          <g key={`guy${gy}`} stroke={FAINT} strokeWidth={0.9} fill="none" strokeDasharray="2 3">
            <line x1={TOWER_X - xAt} y1={y} x2={TOWER_X - xAt - 70} y2={GROUND_Y} />
            <line x1={TOWER_X + xAt} y1={y} x2={TOWER_X + xAt + 70} y2={GROUND_Y} />
          </g>,
        );
      }
    }
  }
  void blockY;
  return <g>{parts}</g>;
}

// --- attachment art (composes with tag art) -----------------------------------
function AttachmentArt({ method, blockX, blockY, type }: { method: string; blockX: number; blockY: number; type: string }) {
  const H = GROUND_Y - TOWER_TOP_Y;
  const halfAt = (y: number) => {
    const f = (GROUND_Y - y) / H;
    return TOWER_BASE_HALF + (TOWER_TOP_HALF - TOWER_BASE_HALF) * f;
  };
  const legX = type === 'monopole' || type === 'mast-pipe' ? TOWER_X : TOWER_X + halfAt(blockY);
  const out: React.ReactNode[] = [];
  if (method === 'antennaMount' || method === 'mastPipeRoosterHead') {
    // mount bracket sticking off the face
    const mountLen = 30;
    out.push(
      <g key="mount" stroke="black" strokeWidth={1.5} fill="none">
        <line x1={legX} y1={blockY + 16} x2={legX + mountLen} y2={blockY + 8} />
        <line x1={legX} y1={blockY - 8} x2={legX + mountLen} y2={blockY + 2} />
        <line x1={legX + mountLen} y1={blockY + 2} x2={legX + mountLen} y2={blockY + 8} />
      </g>,
    );
    if (method === 'mastPipeRoosterHead') {
      // mast pipe standing on the mount + rooster head + block on top
      const pipeX = legX + mountLen - 4;
      const pipeTop = blockY - 46;
      out.push(
        <g key="pipe" stroke="black" strokeWidth={1.5} fill="none">
          <line x1={pipeX - 3} y1={blockY + 4} x2={pipeX - 2} y2={pipeTop} />
          <line x1={pipeX + 3} y1={blockY + 4} x2={pipeX + 2} y2={pipeTop} />
          <path d={`M ${pipeX - 7} ${pipeTop} l 7 -8 l 7 8`} /> {/* rooster head */}
        </g>,
      );
      out.push(<SheaveBlock key="mb" x={pipeX} y={pipeTop - 14} s={1.05} />);
      out.push(<Label key="mpl" x={pipeX + 14} y={pipeTop - 16} text="MAST PIPE / ROOSTER HEAD" />);
    } else {
      out.push(<Label key="ml" x={legX + mountLen + 6} y={blockY - 2} text="ANTENNA MOUNT" />);
    }
  } else if (method === 'faceBasket2') {
    // two slings from both legs down to the block mid-face
    const legLX = TOWER_X - halfAt(blockY);
    out.push(
      <g key="basket" stroke="black" strokeWidth={1.5} fill="none">
        <line x1={legLX} y1={blockY - 22} x2={blockX} y2={blockY - 6} />
        <line x1={legX} y1={blockY - 22} x2={blockX} y2={blockY - 6} />
        <circle cx={legLX} cy={blockY - 22} r={2} fill="black" />
        <circle cx={legX} cy={blockY - 22} r={2} fill="black" />
      </g>,
    );
    out.push(<Label key="bl" x={blockX - 4} y={blockY - 30} text="FACE BASKET — 2 SLINGS" anchor="middle" />);
  } else {
    // leg panel point: sling wrap around the leg
    out.push(
      <g key="wrap" stroke="black" strokeWidth={1.5} fill="none">
        <path d={`M ${legX - 5} ${blockY - 12} q 6 5 0 10`} />
        <line x1={legX - 2} y1={blockY - 7} x2={blockX - 6} y2={blockY - 7} />
      </g>,
    );
    out.push(<Label key="sl" x={legX + 8} y={blockY - 18} text="SLING AT PANEL POINT" />);
  }
  return <g>{out}</g>;
}

export function SchematicLiftDiagram({ lift, computed, width = VW, height = VH, className, forceOverlay = true }: SchematicLiftDiagramProps) {
  const g = lift.geometry;
  const f = computed.forces;
  const type = g.structureType;
  const refused = computed.status === 'refused';

  // elevation fractions → fixed px along the tower art
  const H = GROUND_Y - TOWER_TOP_Y;
  const frac = (ft: number) => Math.min(0.985, Math.max(0.02, ft / Math.max(1, g.structureHeightFt)));
  const blockY = GROUND_Y - frac(g.topBlock.heightFt) * H;
  const loadY = GROUND_Y - frac(g.load.heightFt) * H;

  // horizontal distances → proportional px (relative geometry preserved; the
  // load-below-pull-side relationship is invariant under axis scaling)
  const dH = Math.abs(g.winch.distanceFromTowerFt);
  const dL = Math.abs(g.load.distanceFromTowerFt);
  const dT = Math.abs(g.tag.anchorDistanceFt ?? 0);
  const maxD = Math.max(dH, dL, dT, 1);
  const hScale = (RIGHT_LIMIT - TOWER_X - 60) / maxD;
  const winchX = TOWER_X + dH * hScale;
  const loadX = TOWER_X + Math.max(dL * hScale, 30); // keep load visibly off the face
  const tagX = TOWER_X + dT * hScale;
  const blockX = type === 'monopole' || type === 'mast-pipe' ? TOWER_X : TOWER_X + (TOWER_BASE_HALF + (TOWER_TOP_HALF - TOWER_BASE_HALF) * frac(g.topBlock.heightFt));

  const tagMethod = g.tag.method;
  const parts = lift.hardware.blockParts;
  const ropes: React.ReactNode[] = [];
  const labels: React.ReactNode[] = [];
  const dims: React.ReactNode[] = [];

  // --- hoist rope: winch drum → block → load --------------------------------
  const drumX = winchX - 40, drumY = GROUND_Y - 17;
  const heel = g.heelBlock;
  if (heel) {
    // Heel block sits on the SAME side as the winch (Brian 2026-09-09: the
    // winch→heel rope must never cross the tower — that's the S3 jump
    // arrangement, and the engine refuses heel-opposite-winch as R-7).
    const heelY = GROUND_Y - frac(heel.heightFt) * H;
    const heelX = TOWER_X + (TOWER_BASE_HALF + (TOWER_TOP_HALF - TOWER_BASE_HALF) * frac(heel.heightFt)) + 6;
    ropes.push(<line key="w-h" x1={drumX} y1={drumY} x2={heelX} y2={heelY} stroke="black" strokeWidth={1.6} />);
    ropes.push(<SheaveBlock key="hb" x={heelX} y={heelY} />);
    ropes.push(<line key="h-t" x1={heelX} y1={heelY} x2={blockX} y2={blockY} stroke="black" strokeWidth={1.6} />);
    labels.push(<Label key="hl" x={heelX + 10} y={heelY + 24} text="HEEL BLOCK" anchor="middle" />);
  } else {
    ropes.push(<line key="w-t" x1={drumX} y1={drumY} x2={blockX} y2={blockY - 6} stroke="black" strokeWidth={1.6} />);
  }

  // block → load: n parallel parts (multi-part reeving drawn as n lines)
  const drawParts = Math.min(Math.max(parts || 1, 1), 2);
  const spread = 5;
  for (let i = 0; i < drawParts; i++) {
    const off = drawParts === 1 ? 0 : (i - 0.5) * spread * 2;
    ropes.push(
      <line
        key={`tl${i}`}
        x1={blockX + off * 0.4} y1={blockY + 8}
        x2={loadX + off} y2={tagMethod === 'self' || tagMethod === 'selfTrolley' ? loadY - 34 : loadY - 7}
        stroke="black" strokeWidth={1.5}
      />,
    );
  }

  // --- trolley / tag art ------------------------------------------------------
  if (tagMethod === 'self' || tagMethod === 'selfTrolley') {
    // trolley rides the LOAD LINE above the load; load hangs from the trolley
    const tx = (blockX + loadX) / 2 + (loadX - blockX) * 0.18;
    const ty = loadY - 34;
    ropes.push(<TrolleyBlock key="tr" x={tx} y={ty} />);
    ropes.push(<line key="tr-load" x1={tx} y1={ty + 6} x2={loadX} y2={loadY - 7} stroke="black" strokeWidth={1.5} />);
    labels.push(<Label key="trl" x={tx + 12} y={ty + 3} text="TROLLEY BLOCK" />);
    labels.push(<Label key="srn" x={tx + 12} y={ty + 15} text="LOAD RIDES LOAD LINE" color={GRAY} fs={8} />);
  } else if (tagMethod === 'straight') {
    // dashed labeled tag line: load → ground hold point (winch side)
    ropes.push(<line key="tag" x1={loadX + 10} y1={loadY + 8} x2={tagX || loadX + 60} y2={GROUND_Y - 4} stroke="black" strokeWidth={1.3} strokeDasharray="7 5" />);
    ropes.push(<path key="tagstake" d={`M ${(tagX || loadX + 60) - 5} ${GROUND_Y} l 5 -7 l 5 7`} fill="none" stroke="black" strokeWidth={1.4} />);
    labels.push(<Label key="tagl" x={(loadX + (tagX || loadX + 60)) / 2 + 8} y={(loadY + GROUND_Y) / 2} text="TAG LINE" />);
    labels.push(<Label key="tagh" x={(tagX || loadX + 60) + 10} y={GROUND_Y - 8} text="TAG HOLD" />);
  } else if (tagMethod === 'staticTrolley') {
    // SEPARATE static rope: ground tag anchor → upper anchor near tower top →
    // back down; trolley rides the static line; load hangs from the trolley;
    // the load line does NOT reeve through the trolley.
    const upperY = Math.min(blockY - 26, TOWER_TOP_Y + 18);
    const upperX = TOWER_X + (TOWER_TOP_HALF + 8);
    const anchorX = tagX && tagX > TOWER_X + 40 ? tagX : TOWER_X + maxD * hScale * 0.8 + 40;
    // trolley position on the static line span (upper anchor → ground anchor)
    const tFrac = 0.42;
    const tX = upperX + (anchorX - upperX) * tFrac;
    const tY = upperY + (GROUND_Y - 10 - upperY) * tFrac;
    ropes.push(
      <g key="static" stroke="black" strokeWidth={1.3} strokeDasharray="7 5" fill="none">
        <line x1={upperX} y1={upperY} x2={anchorX} y2={GROUND_Y - 4} />
      </g>,
    );
    ropes.push(<path key="sastake" d={`M ${anchorX - 5} ${GROUND_Y} l 5 -7 l 5 7`} fill="none" stroke="black" strokeWidth={1.4} />);
    ropes.push(<circle key="ua" cx={upperX} cy={upperY} r={2.6} fill="black" />);
    ropes.push(<TrolleyBlock key="str" x={tX} y={tY} />);
    ropes.push(<line key="str-load" x1={tX} y1={tY + 6} x2={loadX + 12} y2={loadY + 4} stroke="black" strokeWidth={1.5} />);
    labels.push(<Label key="sal" x={upperX + 10} y={upperY - 8} text="STATIC TAG LINE" />);
    labels.push(<Label key="satr" x={tX + 12} y={tY - 8} text="STATIC TAG TROLLEY" />);
    labels.push(<Label key="saa" x={anchorX + 10} y={GROUND_Y - 8} text="TAG ANCHOR" />);
  }

  // --- winch + load + block art ------------------------------------------------
  const truck = <TruckWinch key="truck" x={winchX} y={GROUND_Y} />;
  labels.push(<Label key="wl" x={winchX - 20} y={GROUND_Y + 24} text="CAPSTAN WINCH (TRUCK)" anchor="middle" />);

  // --- dimension callouts (the scale truth) -----------------------------------
  dims.push(<DimV key="dh-tb" x={64} y1={GROUND_Y} y2={blockY} label={`H_TB ${g.topBlock.heightFt} FT`} />);
  dims.push(<DimV key="dh-l" x={104} y1={GROUND_Y} y2={loadY} label={`H_L ${g.load.heightFt} FT`} />);
  dims.push(<DimH key="dd-h" y={VH - 66} x1={TOWER_X} x2={winchX} label={`D_H ${dH} FT`} />);
  dims.push(<DimH key="dd-l" y={VH - 44} x1={TOWER_X} x2={loadX} label={`D_L ${dL} FT`} />);
  if (tagMethod === 'straight' || tagMethod === 'staticTrolley') {
    dims.push(<DimH key="dd-t" y={VH - 22} x1={TOWER_X} x2={tagX || loadX + 60} label={`D_T ${dT} FT`} />);
  }
  dims.push(<DimV key="dh-tower" x={146} y1={GROUND_Y} y2={TOWER_TOP_Y} label={`TOWER ${g.structureHeightFt} FT`} />);

  // --- node labels --------------------------------------------------------------
  labels.push(<Label key="tbl" x={blockX + 14} y={blockY - 10} text="TOP BLOCK" />);
  labels.push(<Label key="ll" x={(blockX + loadX) / 2 + 6} y={(blockY + loadY) / 2} text="LOAD LINE" color={GRAY} fs={8.5} />);
  labels.push(<Label key="ldl" x={loadX} y={loadY + 34} text={`LOAD ${lift.loading.grossLoadLbs} LB`} anchor="middle" />);
  labels.push(<Label key="gr" x={TOWER_X - 60} y={GROUND_Y + 14} text="GRADE" color={GRAY} fs={8.5} />);

  // --- force callouts (only color) ------------------------------------------------
  const forces: React.ReactNode[] = [];
  if (forceOverlay && !refused) {
    forces.push(<Label key="ftb" x={blockX + 14} y={blockY + 2} text={`TOP BLOCK ${fmt(f.topBlockResultantLbs)} LB`} color={BLUE} fs={8.5} />);
    forces.push(<Label key="fhp" x={winchX - 20} y={GROUND_Y - 40} text={`HOIST PULL ${fmt(f.winchPullLbs)} LB`} color={BLUE} fs={8.5} anchor="middle" />);
    if (f.tagLineLbs != null) {
      forces.push(<Label key="ftag" x={(loadX + (tagX || loadX + 60)) / 2 + 8} y={(loadY + GROUND_Y) / 2 + 12} text={`TAG ${fmt(f.tagLineLbs)} LB`} color={BLUE} fs={8.5} />);
    }
    if (f.slingLegLbs != null) {
      forces.push(<Label key="fsl" x={blockX + 14} y={blockY + 14} text={`SLING ${fmt(f.slingLegLbs)} LB`} color={BLUE} fs={8.5} />);
    }
    if (f.heelBlockLbs != null) {
      forces.push(<Label key="fhl" x={TOWER_X - 70} y={loadY > GROUND_Y - 120 ? GROUND_Y - 130 : loadY + 40} text={`HEEL ${fmt(f.heelBlockLbs)} LB`} color={BLUE} fs={8.5} />);
    }
  }

  const refusal = refused ? computed.refusals[0] : null;

  return (
    <svg
      width={width} height={height} viewBox={`0 ${CROP_TOP} ${VW} ${VH - CROP_TOP - CROP_BOTTOM}`}
      className={className ?? 'lift-diagram'}
      role="img" aria-label="Schematic lift diagram"
      style={{ background: 'white' }}
      data-testid="schematic-lift-diagram"
    >
      {/* grade line */}
      <line x1={16} y1={GROUND_Y} x2={VW - 16} y2={GROUND_Y} stroke="black" strokeWidth={1.8} />
      <TowerArt type={type} blockY={blockY} />
      {ropes}
      <SheaveBlock x={blockX} y={blockY} s={1.15} />
      <AttachmentArt
        method={g.topBlock.attachment?.method ?? 'legPanelPoint'}
        blockX={blockX} blockY={blockY} type={type}
      />
      {truck}
      <LoadBox x={loadX} y={loadY} />
      {dims}
      {labels}
      {forces}
      {refusal && (
        <g data-testid="schematic-refusal">
          <rect x={VW / 2 - 210} y={92} width={420} height={44} fill="white" stroke="#b71c1c" strokeWidth={1.5} />
          <text x={VW / 2} y={110} fontSize={10} fontFamily="monospace" fill="#b71c1c" textAnchor="middle" fontWeight="bold">
            REFUSED — geometry not possible
          </text>
          <text x={VW / 2} y={126} fontSize={8.5} fontFamily="monospace" fill="#b71c1c" textAnchor="middle">
            {refusal.message.slice(0, 96)}{refusal.message.length > 96 ? '…' : ''}
          </text>
        </g>
      )}
    </svg>
  );
}
